import { useCallback, useEffect, useRef, useState } from "react";

import useLocale from "@/hooks/useLocale";
import { normalize } from "@/utils";

import styles from "./Dropdown.module.css";

import type { LocalizedField } from "@/utils/types";

export interface Option {
  value: string;
  label: LocalizedField;
}

interface DropdownProps {
  onChange: (value: string) => void;
  options: Option[];
  name: string;
  placeholder?: string;
  variant?: "normal" | "white" | "outline";
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  value?: string;
}

const Dropdown = ({
  onChange,
  options,
  name,
  placeholder = "Select a value",
  variant = "normal",
  required = false,
  disabled = false,
  autocomplete = "off",
  value,
}: DropdownProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const targetRef = useRef<HTMLInputElement>(null);
  const locale = useLocale();

  const handleChange = useCallback(
    (newValue: string, event?: React.MouseEvent | React.KeyboardEvent) => {
      if (disabled) return;
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      setIsFocused(false);
      onChange(newValue);
    },
    [disabled, onChange],
  );

  const handleFocus = useCallback(() => {
    if (disabled) return;
    setIsFocused(true);
  }, [disabled]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextElement = event.currentTarget
          .nextElementSibling as HTMLElement;
        if (nextElement) nextElement.focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const prevElement = event.currentTarget
          .previousElementSibling as HTMLElement;
        if (prevElement) prevElement.focus();
      } else if (event.key === "Enter" || event.code === "Space") {
        const optionValue = event.currentTarget.getAttribute("data-value");
        if (optionValue) {
          handleChange(optionValue);
          const nextElement = targetRef.current
            ?.nextElementSibling as HTMLElement;
          if (nextElement) nextElement.focus();
        }
      }
    },
    [handleChange],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.length === 1) return;

      const optionValue = options.find((p) => {
        const localeLabel = Object.values(p.label);
        return localeLabel.some((l) =>
          normalize(l)
            .toLowerCase()
            .startsWith(normalize(event.target.value).toLowerCase()),
        );
      });
      if (optionValue) {
        handleChange(optionValue.value);
      }
    },
    [options, handleChange],
  );

  const inputClasses = () => {
    let className = "";

    if (isFocused) {
      className += " h-min h-max-[320px] border-blue";
    } else {
      className += ` h-min h-max-[60px] rounded-b-[25px] border-b-[2px] ${variant === "outline" ? "border-blue" : "border-gray-200"}`;
    }

    const variants = {
      normal: " bg-gray-200",
      white: " bg-white",
      outline: " bg-white",
    };

    className += variants[variant];

    return className;
  };

  const menuClasses = () => {
    let className = "";

    if (isFocused) {
      className += " opacity-100 max-h-[245px]";
    } else {
      className += " opacity-0 pointer-events-none max-h-0";
    }

    const variants = {
      normal: " bg-gray-200",
      white: " bg-white",
      outline: " bg-white",
    };

    className += variants[variant];

    return className;
  };

  const label = () => {
    const currentOptionValue = options.find((p) => p.value === value);
    return currentOptionValue
      ? currentOptionValue.label[locale as keyof LocalizedField]
      : "";
  };

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    // Use a small delay to avoid conflicts with click events
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className={`relative ${isFocused ? "z-50" : ""}`}>
      <div className="h-[60px] mb-4" />
      <div
        className={`absolute overflow-y-hidden mt-2 top-0 w-full flex flex-col justify-start mb-8 rounded-[25px] border-[2px] transition-all ${inputClasses()}`}
        onClick={(e) => {
          // Only focus if clicking on the container or input, not on menu options
          if (e.target === e.currentTarget || e.target === targetRef.current) {
            handleFocus();
          }
        }}
      >
        <div className="px-6 pb-4 cursor-pointer" onClick={handleFocus}>
          <input
            ref={targetRef}
            className={`${styles.formSelect} mt-4 bg-transparent appearance-none bg-no-repeat w-[100%] placeholder:text-blue outline-none text-blue font-semibold cursor-pointer transition-all`}
            placeholder={placeholder}
            autoComplete={autocomplete}
            name={name}
            value={isFocused ? "" : label()}
            required={required}
            onFocus={handleFocus}
            readOnly={autocomplete === "off"}
            disabled={disabled}
            onChange={handleInputChange}
          />
        </div>
        <div
          className={`menu w-full rounded-b-[25px] top-[50px] transition-all z-20 overflow-y-auto ${menuClasses()}`}
        >
          <div className="pb-4">
            {options.map((option, index) => (
              <div
                key={option.value}
                className={`${index < options.length - 1 ? "border-b-2" : "pb-0"} border-gray-300 p-2 px-6 cursor-pointer ${option.value === value ? "text-blue" : ""}`}
                tabIndex={isFocused ? 0 : -1}
                data-value={option.value}
                onClick={(e) => {
                  handleChange(option.value, e);
                }}
                onKeyDown={handleKeyDown}
              >
                {option.label[locale as keyof LocalizedField]}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
